import {
  AppLocale,
  DEFAULT_LOCALE,
  isAppLocale,
  localeFromLanguageTag,
  timeZoneLocaleMap,
} from "./app-locale";

export type LocaleDetectionInput = {
  /** 사용자가 직접 선택해 저장한 언어. 존재하면 항상 우선한다. */
  storedLocale?: string | null;
  /** navigator.languages 또는 [navigator.language] */
  languageTags?: readonly string[];
  /** Intl.DateTimeFormat().resolvedOptions().timeZone */
  timeZone?: string | null;
};

/**
 * 초기 언어를 결정한다. 우선순위는 다음과 같다.
 * 1. 사용자가 저장한 언어 설정
 * 2. 접속자의 브라우저 언어/지역 정보
 * 3. 접속자의 타임존이 알려주는 지역
 * 4. 기본 언어(한국어)
 */
export function resolveInitialLocale(input: LocaleDetectionInput): AppLocale {
  const { storedLocale, languageTags = [], timeZone } = input;

  if (isAppLocale(storedLocale)) return storedLocale;

  for (const languageTag of languageTags) {
    const locale = localeFromLanguageTag(languageTag);
    if (locale) return locale;
  }

  if (timeZone) {
    const locale = timeZoneLocaleMap[timeZone];
    if (locale) return locale;
  }

  return DEFAULT_LOCALE;
}

/** 현재 브라우저 환경에서 감지 입력값을 수집한다. */
export function readLocaleDetectionInput(storedLocale: string | null): LocaleDetectionInput {
  if (typeof navigator === "undefined") {
    return { storedLocale };
  }

  const languageTags =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];

  return { storedLocale, languageTags, timeZone: readTimeZone() };
}

function readTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}
